import * as XLSX from "xlsx";

/**
 * Reads the first bytes of an ArrayBuffer to detect encoding from BOM.
 * Returns the label used by TextDecoder.
 */
const detectEncodingFromBuffer = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf, 0, 4);
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return "utf-16le";
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return "utf-16be";
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return "utf-8";
  // No BOM — validate as UTF-8
  if (isValidUtf8Bytes(bytes, new Uint8Array(buf))) return "utf-8";
  // Fallback: windows-1252 covers ANSI/Macintosh well enough for Latin scripts
  return "windows-1252";
};

const isValidUtf8Bytes = (_sample: Uint8Array, full: Uint8Array): boolean => {
  let i = 0;
  while (i < Math.min(full.length, 4096)) {
    const b = full[i];
    if (b <= 0x7f) { i += 1; continue; }
    if (b >= 0xc2 && b <= 0xdf) {
      if (i + 1 >= full.length || (full[i + 1] & 0xc0) !== 0x80) return false;
      i += 2; continue;
    }
    if (b >= 0xe0 && b <= 0xef) {
      if (i + 2 >= full.length) return false;
      if ((full[i + 1] & 0xc0) !== 0x80 || (full[i + 2] & 0xc0) !== 0x80) return false;
      i += 3; continue;
    }
    if (b >= 0xf0 && b <= 0xf4) {
      if (i + 3 >= full.length) return false;
      if ((full[i + 1] & 0xc0) !== 0x80 || (full[i + 2] & 0xc0) !== 0x80 || (full[i + 3] & 0xc0) !== 0x80) return false;
      i += 4; continue;
    }
    return false;
  }
  return true;
};

/**
 * Reads a File and returns its text content with automatic encoding detection.
 * Handles UTF-8 (with/without BOM), UTF-16 LE/BE, and Windows-1252 (ANSI/Macintosh/MS-DOS).
 */
export const readFileWithEncoding = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const buf = e.target?.result as ArrayBuffer;
      const encoding = detectEncodingFromBuffer(buf);
      const decoder = new TextDecoder(encoding);
      resolve(decoder.decode(buf));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

export const fileToArrayBuffer = (file: File): Promise<string> => {
  return readFileWithEncoding(file);
};

export const csvToArray = (csvString: string): string[][] => {
  const lines = csvString.trim().split("\n");
  return lines.map((line) => {
    const row: string[] = [];
    let current = "";
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === "," && !insideQuotes) {
        row.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    if (current) {
      row.push(current.trim());
    }

    return row;
  });
};

export const arrayToCsv = (rows: string[][]): string => {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap in quotes if contains comma or quote
          if (cell.includes(",") || cell.includes('"') || cell.includes("\n")) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        })
        .join(",")
    )
    .join("\n");
};

export const fileToSheetData = async (file: File) => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  let jsonData: any[] = [];

  if (ext === "csv") {
    const text = await readFileWithEncoding(file);
    const workbook = XLSX.read(text, { type: "string" });
    const wsname = workbook.SheetNames[0];
    const ws = workbook.Sheets[wsname];
    jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
  } else {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const wsname = workbook.SheetNames[0];
    const ws = workbook.Sheets[wsname];
    jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
  }

  // Format for x-data-spreadsheet
  const sheetData = {
    name: file.name,
    rows: {} as any,
  };

  jsonData.forEach((row, r) => {
    sheetData.rows[r] = { cells: {} };
    row.forEach((cell: any, c: number) => {
      sheetData.rows[r].cells[c] = { text: cell?.toString() ?? "" };
    });
  });

  return sheetData;
};

export const spreadsheetToFile = (
  spreadsheetData: any[],
  fileName: string,
  fileType: "csv" | "xlsx" = "xlsx"
): File => {
  if (!spreadsheetData || !spreadsheetData[0]) {
    throw new Error("No data to save");
  }

  const data = spreadsheetData[0].rows || {};
  
  // First, find the maximum column index across all rows
  let maxColIndex = 0;
  Object.keys(data).forEach((r) => {
    const row = data[r]?.cells || {};
    Object.keys(row).forEach((c) => {
      const colIndex = parseInt(c, 10);
      if (colIndex > maxColIndex) {
        maxColIndex = colIndex;
      }
    });
  });

  const rows: string[][] = [];

  // Get sorted row indices
  const rowIndices = Object.keys(data)
    .map(key => parseInt(key, 10))
    .sort((a, b) => a - b);

  rowIndices.forEach((rowIndex) => {
    const row = data[rowIndex]?.cells || {};
    const arr: string[] = [];
    
    // Iterate through ALL columns from 0 to maxColIndex
    for (let c = 0; c <= maxColIndex; c++) {
      arr.push(row[c]?.text || "");
    }
    
    // Check if the row is completely empty
    const isEmptyRow = arr.every(cell => cell.trim() === "");
    
    // Only add non-empty rows
    if (!isEmptyRow) {
      rows.push(arr);
    }
  });

  // Make sure we have at least some data
  if (rows.length === 0) {
    throw new Error("No data to save - spreadsheet is empty");
  }

  if (fileType === "csv") {
    const csvContent = rows
      .map((row) =>
        row
          .map((cell) => {
            if (cell.includes(",") || cell.includes('"') || cell.includes("\n")) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          })
          .join(",")
      )
      .join("\n");

    console.log(`CSV generated with ${rows.length} non-empty rows`);
    return new File([csvContent], fileName, { type: "text/csv" });
  } else {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    
    console.log(`XLSX generated with ${rows.length} non-empty rows`);
    return new File([blob], fileName, {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  }
};

// Suppress ResizeObserver errors
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
      e.stopImmediatePropagation();
    }
  });
}
